import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react';

export interface WorkspaceDraftState {
  dirty: boolean;
  busy: boolean;
}

type LeaveAction = () => void | Promise<unknown>;
type GetDraftState = () => WorkspaceDraftState;

interface GuardOptions {
  /** Keep the existing toast/notification UI when an update is in flight. */
  onBusy?: () => void;
}

interface DraftGuard {
  getState: GetDraftState;
  onBusy: () => void;
}

interface GuardContext {
  register: (guard: DraftGuard) => () => void;
  requestLeave: (onLeave: LeaveAction) => boolean;
}

const Context = createContext<GuardContext | null>(null);
const CONFIRM_LEAVE = 'Leave this workspace? Unsaved changes will be lost.';
const BUSY_MESSAGE = 'Wait for the document to finish saving or updating.';

function attemptLeave(guards: Iterable<DraftGuard>, onLeave: LeaveAction): boolean {
  // Read at the time of the navigation. Some editors report through refs,
  // so a rerender is neither necessary nor sufficient to get fresh state.
  const states = Array.from(guards, (guard) => ({ guard, state: guard.getState() }));
  const busy = states.find(({ state }) => state.busy);
  if (busy) {
    busy.guard.onBusy();
    return false;
  }
  if (states.some(({ state }) => state.dirty) && !window.confirm(CONFIRM_LEAVE)) return false;
  void onLeave();
  return true;
}

function protectUnload(event: BeforeUnloadEvent, states: Iterable<WorkspaceDraftState>): void {
  if (!Array.from(states).some((state) => state.dirty || state.busy)) return;
  event.preventDefault();
  // The browser supplies its own localized warning; custom text is ignored.
  event.returnValue = '';
}

/** Scope this provider around the Architect topbar AND its page content.
 * Explicit navigation (project Back, Workspaces, Log out) shares the same
 * current draft state. Stage changes do not call requestLeave, since their
 * editors remain mounted and no draft is discarded.
 *
 * Browser Back to another document, refresh and tab close are protected by
 * beforeunload. We deliberately do not push/replace history or intercept
 * popstate: this workspace uses React state for its internal views, and a
 * same-document hash/history change does not discard those views.
 */
export function WorkspaceDraftGuardProvider({ children }: { children: React.ReactNode }) {
  const guards = useRef(new Set<DraftGuard>());
  const register = useCallback((guard: DraftGuard) => {
    guards.current.add(guard);
    return () => { guards.current.delete(guard); };
  }, []);
  const requestLeave = useCallback((onLeave: LeaveAction) => attemptLeave(guards.current, onLeave), []);
  const value = useRef<GuardContext>({ register, requestLeave }).current;

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      protectUnload(event, Array.from(guards.current, (guard) => guard.getState()));
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

/** Register a mounted editor's latest aggregate state and guard its Back
 * action. Safe to use outside the provider in standalone editor surfaces.
 */
export function useWorkspaceDraftGuard(getState: GetDraftState, options: GuardOptions = {}) {
  const context = useContext(Context);
  const latest = useRef({ getState, options });
  latest.current = { getState, options };
  const guard = useRef<DraftGuard>({
    getState: () => latest.current.getState(),
    onBusy: () => {
      if (latest.current.options.onBusy) latest.current.options.onBusy();
      else window.alert(BUSY_MESSAGE);
    },
  }).current;

  useEffect(() => {
    if (context) return context.register(guard);
    const onBeforeUnload = (event: BeforeUnloadEvent) => protectUnload(event, [guard.getState()]);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [context, guard]);

  return useCallback(
    (onLeave: LeaveAction) => context ? context.requestLeave(onLeave) : attemptLeave([guard], onLeave),
    [context, guard],
  );
}

/** For the workspace chrome, where no local editable form is present. */
export function useWorkspaceLeaveGuard() {
  const context = useContext(Context);
  return useCallback((onLeave: LeaveAction) => {
    if (context) return context.requestLeave(onLeave);
    void onLeave();
    return true;
  }, [context]);
}
